import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Send, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [50, 100, 150];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso || ""; }
}

export default function AdminGiftCards() {
  const [data, setData] = useState({ items: [], count: 0, outstanding_value: 0, issued_value: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [amount, setAmount] = useState(100);
  const [recipient, setRecipient] = useState({ name: "", email: "", buyer: "", note: "", expires_at: "" });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [redeemFor, setRedeemFor] = useState(null); // card being redeemed
  const [redeemAmt, setRedeemAmt] = useState("");
  const [redeemService, setRedeemService] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/admin/gift-cards`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data.items;
    return data.items.filter((c) =>
      (c.code || "").toLowerCase().includes(needle) ||
      (c.recipient_email || "").toLowerCase().includes(needle) ||
      (c.recipient_name || "").toLowerCase().includes(needle),
    );
  }, [data.items, q]);

  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/gift-cards`, {
        amount: Number(amount),
        recipient_name: recipient.name.trim(),
        recipient_email: recipient.email.trim(),
        buyer_name: recipient.buyer.trim(),
        note: recipient.note.trim(),
        expires_at: recipient.expires_at.trim(),
      }, { withCredentials: true });
      const emailed = res.data?.email_sent;
      toast.success(`Gift card created (${res.data?.card?.code}).`, {
        description: emailed
          ? `Emailed to ${recipient.email}.`
          : "Email delivery not configured — copy the code and share manually.",
      });
      setRecipient({ name: "", email: "", buyer: "", note: "", expires_at: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  const resendEmail = async (code) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/gift-cards/${code}/resend`, {}, { withCredentials: true });
      if (res.data?.email_sent) toast.success("Email resent.");
      else toast.error("Email service unavailable.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend");
    } finally { setBusy(false); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success(`${code} copied.`); }
    catch { toast.error("Clipboard access denied."); }
  };

  const redeem = async (e) => {
    e.preventDefault();
    if (!redeemFor || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/gift-cards/${redeemFor.code}/redeem`,
        { amount: Number(redeemAmt), service: redeemService.trim() },
        { withCredentials: true });
      toast.success(`Redeemed $${redeemAmt} against ${redeemFor.code}.`);
      setRedeemFor(null); setRedeemAmt(""); setRedeemService("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to redeem");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="admin-gift-cards">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">Gift certificates</h2>
          <p className="text-sm text-[#0F1F4E]/70 mt-1 max-w-xl font-light">
            Generate a $50/$100/$150 gift certificate. The recipient gets an email
            with a redemption code you can look up here when they book.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span data-testid="gift-outstanding-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2EBD3] border border-[#D9CFA0] text-xs uppercase tracking-wider">
            ${data.outstanding_value} outstanding
          </span>
          <span data-testid="gift-issued-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EAD584] text-[#0F1F4E] text-xs uppercase tracking-wider">
            <Gift className="w-3.5 h-3.5" /> ${data.issued_value} issued · {data.count}
          </span>
        </div>
      </div>

      <form onSubmit={create} data-testid="gift-create-form" className="bg-white border border-[#D9CFA0] rounded-lg p-5 md:p-6 mb-8 grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-12">
          <div className="overline block mb-2">Amount</div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => setAmount(p)} data-testid={`gift-preset-${p}`} className={`px-4 py-2 rounded-full text-sm border transition-colors ${Number(amount) === p ? "bg-[#0F1F4E] text-[#EAD584] border-[#0F1F4E]" : "border-[#D9CFA0] hover:border-[#0F1F4E]"}`}>${p}</button>
            ))}
            <label className="flex items-center gap-2 px-3 py-2 rounded-full border border-[#D9CFA0]">
              <span className="text-xs uppercase tracking-wider text-[#0F1F4E]/70">Custom</span>
              <input type="number" min="1" max="5000" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="gift-amount-input" className="w-20 bg-transparent outline-none text-sm" />
            </label>
          </div>
        </div>
        <label className="block md:col-span-6">
          <span className="overline block mb-1.5">Recipient name</span>
          <input required type="text" value={recipient.name} onChange={(e) => setRecipient({ ...recipient, name: e.target.value })} data-testid="gift-recipient-name" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" placeholder="Sarah Miller" />
        </label>
        <label className="block md:col-span-6">
          <span className="overline block mb-1.5">Recipient email</span>
          <input required type="email" value={recipient.email} onChange={(e) => setRecipient({ ...recipient, email: e.target.value })} data-testid="gift-recipient-email" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" placeholder="sarah@email.com" />
        </label>
        <label className="block md:col-span-6">
          <span className="overline block mb-1.5">Buyer / from (optional)</span>
          <input type="text" value={recipient.buyer} onChange={(e) => setRecipient({ ...recipient, buyer: e.target.value })} data-testid="gift-buyer-name" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" placeholder="John Miller" />
        </label>
        <label className="block md:col-span-6">
          <span className="overline block mb-1.5">Expires (optional, YYYY-MM-DD)</span>
          <input type="date" value={recipient.expires_at} onChange={(e) => setRecipient({ ...recipient, expires_at: e.target.value })} data-testid="gift-expires-input" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" />
        </label>
        <label className="block md:col-span-12">
          <span className="overline block mb-1.5">Personal note (optional)</span>
          <textarea rows={2} value={recipient.note} onChange={(e) => setRecipient({ ...recipient, note: e.target.value })} data-testid="gift-note-input" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm resize-none" placeholder="Happy birthday!" />
        </label>
        <div className="md:col-span-12 flex justify-end">
          <button type="submit" disabled={busy} data-testid="gift-create-submit" className="btn-shine inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs uppercase tracking-wider bg-[#0F1F4E] text-[#EAD584] hover:bg-[#1B3370] transition-colors disabled:opacity-50">
            <Send className="w-3.5 h-3.5" /> {busy ? "Creating…" : "Create & email"}
          </button>
        </div>
      </form>

      <div className="mb-4 max-w-md">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} data-testid="gift-filter" placeholder="Filter by code, email, or name…" className="w-full bg-white border border-[#D9CFA0] rounded-md px-4 py-2.5 text-sm outline-none focus:border-[#0F1F4E]" />
      </div>

      {err && <p className="text-red-600 text-sm mb-4">{err}</p>}
      {loading ? <p className="text-[#0F1F4E]/70">Loading…</p> : filtered.length === 0 ? (
        <p className="text-[#0F1F4E]/70" data-testid="gift-empty">No gift certificates yet.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#D9CFA0] rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EBD3]">
              <tr>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Code</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Recipient</th>
                <th className="text-center px-4 py-3 font-medium uppercase tracking-wider text-xs">Amount</th>
                <th className="text-center px-4 py-3 font-medium uppercase tracking-wider text-xs">Balance</th>
                <th className="text-center px-4 py-3 font-medium uppercase tracking-wider text-xs">Status</th>
                <th className="text-left px-4 py-3 font-medium uppercase tracking-wider text-xs">Issued</th>
                <th className="text-right px-4 py-3 font-medium uppercase tracking-wider text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.code} data-testid={`gift-row-${c.code}`} className="border-t border-[#D9CFA0]/60 align-top">
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{c.code}</td>
                  <td className="px-4 py-3">
                    <div>{c.recipient_name || "—"}</div>
                    <a href={`mailto:${c.recipient_email}`} className="text-xs underline text-[#0F1F4E]/70 break-all">{c.recipient_email}</a>
                    {c.buyer_name && <div className="text-[10px] uppercase tracking-wider text-[#0F1F4E]/50 mt-1">From {c.buyer_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-center tabular-nums">${c.amount}</td>
                  <td className="px-4 py-3 text-center tabular-nums font-medium">${c.remaining_balance}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${c.status === "active" ? "bg-[#EAD584] text-[#0F1F4E]" : "bg-[#0F1F4E]/10 text-[#0F1F4E]/70"}`}>{c.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#0F1F4E]/70 whitespace-nowrap">{fmtDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button type="button" onClick={() => copyCode(c.code)} data-testid={`gift-copy-${c.code}`} title="Copy code" className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-[#0F1F4E]/70 hover:text-[#0F1F4E]"><Copy className="w-3 h-3" /></button>
                    <button type="button" onClick={() => resendEmail(c.code)} disabled={busy} data-testid={`gift-resend-${c.code}`} className="text-xs uppercase tracking-wider text-[#0F1F4E]/70 hover:text-[#C9A65A] mx-2">Resend</button>
                    <button type="button" onClick={() => { setRedeemFor(c); setRedeemAmt(String(c.remaining_balance)); }} disabled={c.status !== "active" || c.remaining_balance <= 0} data-testid={`gift-redeem-open-${c.code}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs uppercase tracking-wider bg-[#0F1F4E] text-[#EAD584] hover:bg-[#1B3370] disabled:opacity-40">Redeem</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {redeemFor && (
        <div data-testid="gift-redeem-modal" className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRedeemFor(null)}>
          <form onSubmit={redeem} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
            <h3 className="font-serif text-2xl">Redeem {redeemFor.code}</h3>
            <p className="text-sm text-[#0F1F4E]/70">Current balance: <strong>${redeemFor.remaining_balance}</strong></p>
            <label className="block">
              <span className="overline block mb-1.5">Amount to redeem</span>
              <input required type="number" min="1" max={redeemFor.remaining_balance} value={redeemAmt} onChange={(e) => setRedeemAmt(e.target.value)} data-testid="gift-redeem-amount" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-base" />
            </label>
            <label className="block">
              <span className="overline block mb-1.5">Applied to (optional)</span>
              <input type="text" value={redeemService} onChange={(e) => setRedeemService(e.target.value)} data-testid="gift-redeem-service" className="w-full bg-transparent border-b border-[#0F1F4E]/30 focus:border-[#0F1F4E] outline-none py-2 text-sm" placeholder="60 Minute Massage — Oct 30 visit" />
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setRedeemFor(null)} className="px-4 py-2 rounded-full text-xs uppercase tracking-wider text-[#0F1F4E]/70">Cancel</button>
              <button type="submit" disabled={busy} data-testid="gift-redeem-submit" className="btn-shine px-5 py-2 rounded-full text-xs uppercase tracking-wider bg-[#0F1F4E] text-[#EAD584] disabled:opacity-50">Confirm redemption</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
